import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Images, RefreshCw, XCircle, Search, Film, Tv, Link2, Sparkles, ChevronLeft, ChevronRight, Users } from "lucide-react";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { GlassCard, PageHeader, Spinner, Modal } from "../components/common";
import { DomainGate } from "../components/DomainGate";

const SEM_PASTA = "(Sem pasta)";

function PosterCard({ item, busy, onRetry, onManual }) {
  const [imgErr, setImgErr] = useState(false);
  return (
    <div className="rounded-xl border border-slate-800 bg-slate-900/50 overflow-hidden flex flex-col" data-testid={`capa-missing-${item.id}`}>
      <div className="aspect-[2/3] bg-slate-800/60 flex flex-col items-center justify-center gap-2 text-slate-600">
        {item.poster_url && !imgErr ? (
          <img src={item.poster_url} alt="" data-testid={`capa-poster-${item.id}`} className="w-full h-full object-cover" loading="lazy" onError={() => setImgErr(true)} />
        ) : (
          <>
            {item.media_type === "tv" ? <Tv size={26} /> : <Film size={26} />}
            <span className="text-[9px] font-mono2 uppercase tracking-widest">sem capa</span>
          </>
        )}
      </div>
      <div className="p-2.5 space-y-1.5 flex-1 flex flex-col">
        <p className="text-[11px] text-slate-200 font-medium leading-tight line-clamp-2" title={item.title}>
          {item.title}{item.year ? ` (${item.year})` : ""}
        </p>
        <p className="text-[9px] font-mono2 text-slate-500 truncate">{item.category || SEM_PASTA}</p>
        {(item.poster_fails || 0) > 0 && <p className="text-[8px] font-mono2 text-amber-400/80" data-testid={`capa-fails-${item.id}`}>falhou {item.poster_fails}× na geração</p>}
        <div className="flex gap-1.5 pt-1 mt-auto">
          <button type="button" data-testid={`capa-retry-${item.id}`} onClick={() => onRetry(item.id)} disabled={busy === item.id}
            className="flex-1 text-[10px] px-2 py-1.5 rounded-lg btn-neon flex items-center justify-center gap-1">
            {busy === item.id ? <Spinner /> : <><Sparkles size={11} /> Gerar</>}
          </button>
          <button type="button" data-testid={`capa-manual-${item.id}`} title="Colar URL de imagem manual" onClick={() => onManual(item)} disabled={!!busy}
            className="text-[10px] px-2 py-1.5 rounded-lg btn-ghost-dark text-slate-400 hover:text-cyan-300">
            <Link2 size={11} />
          </button>
        </div>
      </div>
    </div>
  );
}

export default function Capas() {
  const { user } = useAuth();
  const isAdmin = user.role === "super_admin";
  const [capas, setCapas] = useState(null);
  const [missing, setMissing] = useState(null);
  const [busy, setBusy] = useState("");
  const [query, setQuery] = useState("");
  const [catFilter, setCatFilter] = useState("");
  const [failedOnly, setFailedOnly] = useState(false); // false=novas, true=falhadas, "vod"=capa padrão VOD gerada
  const [page, setPage] = useState(1);
  const [accountId, setAccountId] = useState("");
  const [accounts, setAccounts] = useState(null);
  const [manual, setManual] = useState(null); // {id, title}
  const [manualUrl, setManualUrl] = useState("");

  const qs = useCallback((extra = "") => `${extra ? extra + "&" : "?"}${accountId ? `user_id=${accountId}` : ""}`.replace(/[?&]$/, ""), [accountId]);

  const loadStatus = useCallback(async () => {
    try {
      const { data } = await api.get(`/posters/status${qs()}`);
      setCapas(data);
    } catch (e) { toast.error(formatApiError(e)); }
  }, [qs]);

  const loadMissing = useCallback(async (p = page) => {
    try {
      const { data } = await api.get(`/posters/missing${qs(`?q=${encodeURIComponent(query)}&category=${encodeURIComponent(catFilter)}&failed=${failedOnly === "vod" ? "vod" : failedOnly ? "1" : "0"}&page=${p}`)}`);
      setMissing(data);
    } catch (e) { toast.error(formatApiError(e)); }
  }, [qs, query, catFilter, failedOnly, page]);

  useEffect(() => { loadStatus(); }, [loadStatus]);
  useEffect(() => {
    const t = setTimeout(() => { setPage(1); loadMissing(1); }, 300);
    return () => clearTimeout(t);
  }, [query, catFilter, failedOnly, accountId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!capas?.job || capas.job.status !== "running") return undefined;
    const t = setInterval(() => { loadStatus(); loadMissing(); }, 4000);
    return () => clearInterval(t);
  }, [capas?.job?.status, loadStatus, loadMissing]);

  useEffect(() => {
    if (isAdmin) {
      api.get("/folders/tree").then((r) => setAccounts(r.data.accounts || [])).catch(() => {});
    }
  }, [isAdmin]);

  const startGenerate = async (category = "", retry = false) => {
    setBusy(retry ? "retry" : (category ? `cat-${category}` : "all"));
    try {
      const params = `${category ? `category=${encodeURIComponent(category)}&` : ""}${retry ? "retry_failed=true" : ""}`.replace(/&$/, "");
      const { data } = await api.post(`/posters/generate${qs(params ? `?${params}` : "")}`);
      toast.success(data.nothing ? "Nada a gerar nesse filtro" : `Geração iniciada para ${data.job?.total ?? ""} título(s)${retry ? " que já tinham falhado" : ""}${category ? ` da pasta "${category}"` : ""}`);
      loadStatus();
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(""); }
  };

  const startVodAll = async () => {
    setBusy("vod");
    try {
      const { data } = await api.post(`/posters/vod-fallback${qs()}`);
      toast.success(data.nothing ? "Nada sem capa" : `A aplicar capa VOD a ${data.job?.total ?? ""} título(s) — direto, sem TMDB`);
      loadStatus();
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(""); }
  };

  const startRepair = async () => {
    setBusy("repair");
    try {
      const { data } = await api.post(`/posters/repair${qs()}`);
      toast.success(data.nothing ? "Nenhuma capa externa para validar" : `A validar ${data.job?.total ?? ""} capa(s) externa(s) — as quebradas são regeneradas`);
      loadStatus();
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(""); }
  };

  const cancelJob = async () => {
    try {
      await api.post(`/posters/cancel${qs()}`);
      toast.success("Geração cancelada — o que já foi gerado fica guardado");
      loadStatus();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const retryOne = async (id) => {
    setBusy(id);
    try {
      await api.post(`/posters/generate/${id}${qs()}`);
      toast.success("Capa gerada — e aplicada a toda a série");
      loadStatus();
      loadMissing();
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(""); }
  };

  const saveManual = async () => {
    if (!manual) return;
    setBusy(manual.id);
    try {
      await api.post(`/posters/manual/${manual.id}${qs()}`, { url: manualUrl.trim() });
      toast.success("Capa manual aplicada");
      setManual(null);
      setManualUrl("");
      loadStatus();
      loadMissing();
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(""); }
  };

  const running = capas?.job?.status === "running";
  const totalPages = missing ? Math.max(1, Math.ceil(missing.total / missing.per_page)) : 1;

  return (
    <div className="space-y-6" data-testid="capas-page">
      <PageHeader eyebrow="TMDB" title="Capas" subtitle="Gerador de posters do acervo — busca inteligente no TMDB (limpa tags do nome, tenta PT/EN e o tipo oposto) e aplica a capa a todos os episódios da mesma série." />
      <DomainGate />

      {!capas ? (
        <div className="flex justify-center py-12"><Spinner /></div>
      ) : (
        <>
          {!capas.tmdb_configured && (
            <p className="text-xs text-amber-300 bg-amber-400/10 border border-amber-400/30 rounded-xl px-4 py-3" data-testid="capas-no-key">
              A chave TMDB ainda não está configurada no servidor. Pede ao administrador para a adicionar (TMDB_API_KEY) — é gratuita em themoviedb.org.
            </p>
          )}

          <div className="grid grid-cols-3 gap-3" data-testid="capas-stats">
            <GlassCard className="p-4 text-center"><p className="text-2xl font-bold text-white" data-testid="capas-total">{capas.total}</p><p className="text-[10px] font-mono2 uppercase tracking-widest text-slate-500">Títulos</p></GlassCard>
            <GlassCard className="p-4 text-center border-emerald-400/20"><p className="text-2xl font-bold text-emerald-300" data-testid="capas-with">{capas.with_poster}</p><p className="text-[10px] font-mono2 uppercase tracking-widest text-slate-500">Com capa</p></GlassCard>
            <GlassCard className="p-4 text-center border-rose-400/20"><p className="text-2xl font-bold text-rose-300" data-testid="capas-missing-count">{capas.missing}</p><p className="text-[10px] font-mono2 uppercase tracking-widest text-slate-500">Sem capa</p></GlassCard>
          </div>

          {running && (
            <div className="rounded-xl border border-cyan-400/25 bg-cyan-400/5 p-4" data-testid="capas-progress">
              <div className="flex justify-between gap-2 text-[11px] text-slate-400 mb-1.5">
                <span className="truncate">A gerar: <b className="text-cyan-300">{capas.job.current || "..."}</b></span>
                <span className="font-mono2 shrink-0">{capas.job.done}/{capas.job.total} · {capas.job.found} ok · {capas.job.failed} falhas</span>
              </div>
              <div className="h-2 rounded-full bg-slate-800 overflow-hidden">
                <div className="h-full bg-cyan-400 transition-all" style={{ width: `${Math.round(100 * (capas.job.done || 0) / Math.max(1, capas.job.total))}%` }} />
              </div>
            </div>
          )}
          {capas.job?.status === "done" && (
            <p className="text-xs text-emerald-300 bg-emerald-400/10 border border-emerald-400/25 rounded-xl px-4 py-2.5" data-testid="capas-last-job">
              Última geração concluída: {capas.job.found} capas encontradas · {capas.job.failed} falhas
            </p>
          )}

          <div className="flex flex-wrap gap-2" data-testid="capas-actions">
            <button data-testid="capas-generate-all-btn" onClick={() => startGenerate("")} disabled={!!busy || !capas.tmdb_configured || capas.missing === 0 || running}
              className="btn-neon px-4 py-2.5 rounded-xl text-xs flex items-center gap-2">
              {busy === "all" ? <Spinner /> : <><Images size={14} /> Gerar novas ({capas.missing - (capas.failed_missing || 0)})</>}
            </button>
            {(capas.failed_missing || 0) > 0 && (
              <button data-testid="capas-retry-failed-btn" onClick={() => startGenerate("", true)} disabled={!!busy || !capas.tmdb_configured || running}
                title="As falhadas ficam de fora da geração normal — este botão retenta só essas" className="btn-ghost-dark px-4 py-2.5 rounded-xl text-xs flex items-center gap-2 text-amber-300">
                {busy === "retry" ? <Spinner /> : <><RefreshCw size={14} /> Retentar falhadas ({capas.failed_missing})</>}
              </button>
            )}
            {(capas.missing || 0) > 0 && (
              <button data-testid="capas-vod-all-btn" onClick={startVodAll} disabled={!!busy || running}
                title="Aplica a capa padrão VOD a TODOS os títulos sem capa (novas + falhadas), sem tentar o TMDB — rápido"
                className="btn-ghost-dark px-4 py-2.5 rounded-xl text-xs flex items-center gap-2 text-emerald-300">
                {busy === "vod" ? <Spinner /> : <><Sparkles size={14} /> Capa VOD p/ todas ({capas.missing})</>}
              </button>
            )}
            <button data-testid="capas-repair-btn" onClick={startRepair} disabled={!!busy || running}
              title="Valida as capas externas (URLs herdados das listas importadas); as mortas são regeneradas via TMDB ou capa VOD"
              className="btn-ghost-dark px-4 py-2.5 rounded-xl text-xs flex items-center gap-2 text-amber-300">
              {busy === "repair" ? <Spinner /> : <><RefreshCw size={14} /> Reparar quebradas</>}
            </button>
            {running && (
              <button data-testid="capas-cancel-btn" onClick={cancelJob} className="btn-ghost-dark px-4 py-2.5 rounded-xl text-xs flex items-center gap-2 text-rose-300">
                <XCircle size={14} /> Cancelar
              </button>
            )}
            <button data-testid="capas-refresh-btn" onClick={() => { loadStatus(); loadMissing(); }} className="btn-ghost-dark px-4 py-2.5 rounded-xl text-xs flex items-center gap-2 text-slate-300">
              <RefreshCw size={14} /> Atualizar
            </button>
          </div>

          {capas.by_category.length > 0 && (
            <GlassCard className="p-4" data-testid="capas-by-category">
              <p className="text-[10px] font-mono2 uppercase tracking-widest text-slate-500 mb-3">Sem capa por pasta — gerar só uma</p>
              <div className="flex flex-wrap gap-2">
                {capas.by_category.map((c) => (
                  <button key={c.name} type="button" data-testid={`capas-cat-${c.name}`} onClick={() => startGenerate(c.name === SEM_PASTA ? SEM_PASTA : c.name)}
                    disabled={!!busy || !capas.tmdb_configured || running}
                    className="text-[11px] px-3 py-1.5 rounded-lg border border-slate-800 bg-slate-900/50 text-slate-300 hover:border-cyan-400/40 hover:text-cyan-300 transition-colors disabled:opacity-40 flex items-center gap-2">
                    {busy === `cat-${c.name}` ? <Spinner /> : null}
                    <span className="truncate max-w-[160px]">{c.name}</span>
                    <span className="font-mono2 text-rose-300">{c.missing}</span>
                  </button>
                ))}
              </div>
            </GlassCard>
          )}

          <GlassCard className="p-4 space-y-4" data-testid="capas-missing-panel">
            <div className="flex flex-wrap items-center gap-2">
              <div className="relative flex-1 min-w-[200px]">
                <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
                <input data-testid="capas-search-input" value={query} onChange={(e) => setQuery(e.target.value)}
                  placeholder="Pesquisar títulos sem capa…" className="input-dark w-full rounded-lg pl-9 pr-3 py-2 text-sm" />
              </div>
              <select data-testid="capas-cat-filter" value={catFilter} onChange={(e) => setCatFilter(e.target.value)} className="input-dark rounded-lg px-3 py-2 text-sm max-w-[200px]">
                <option value="">Todas as pastas</option>
                {capas.by_category.map((c) => <option key={c.name} value={c.name}>{c.name} ({c.missing})</option>)}
              </select>
              <div className="flex rounded-lg overflow-hidden border border-slate-800" data-testid="capas-failed-toggle">
                <button type="button" data-testid="capas-filter-new" onClick={() => setFailedOnly(false)}
                  className={`text-[10px] px-3 py-2 font-mono2 uppercase tracking-widest transition-colors ${!failedOnly ? "bg-cyan-400/15 text-cyan-300" : "text-slate-500 hover:text-slate-300"}`}>Novas</button>
                <button type="button" data-testid="capas-filter-failed" onClick={() => setFailedOnly(true)}
                  className={`text-[10px] px-3 py-2 font-mono2 uppercase tracking-widest transition-colors ${failedOnly === true ? "bg-amber-400/15 text-amber-300" : "text-slate-500 hover:text-slate-300"}`}>Falhadas{capas.failed_missing ? ` (${capas.failed_missing})` : ""}</button>
                <button type="button" data-testid="capas-filter-vod" onClick={() => setFailedOnly("vod")}
                  className={`text-[10px] px-3 py-2 font-mono2 uppercase tracking-widest transition-colors ${failedOnly === "vod" ? "bg-emerald-400/15 text-emerald-300" : "text-slate-500 hover:text-slate-300"}`}>Gerada VOD{capas.vod_generated ? ` (${capas.vod_generated})` : ""}</button>
              </div>
              {isAdmin && accounts && (
                <div className="flex items-center gap-2">
                  <Users size={14} className="text-amber-400" />
                  <select data-testid="capas-account-select" value={accountId} onChange={(e) => { setAccountId(e.target.value); }} className="input-dark rounded-lg px-3 py-2 text-sm max-w-[200px]">
                    <option value="">Minha conta</option>
                    {accounts.map((a) => <option key={a.id} value={a.id}>{a.name || a.email}</option>)}
                  </select>
                </div>
              )}
            </div>

            {!missing ? (
              <div className="flex justify-center py-8"><Spinner /></div>
            ) : missing.items.length === 0 ? (
              <p className="text-sm text-slate-500 text-center py-8" data-testid="capas-empty">Nenhum título sem capa{query ? ` para "${query}"` : ""} — está tudo coberto.</p>
            ) : (
              <>
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3" data-testid="capas-grid">
                  {missing.items.map((it) => (
                    <PosterCard key={it.id} item={it} busy={busy} onRetry={retryOne} onManual={(i) => { setManual(i); setManualUrl(""); }} />
                  ))}
                </div>
                {totalPages > 1 && (
                  <div className="flex items-center justify-center gap-3 pt-2" data-testid="capas-pagination">
                    <button data-testid="capas-prev-page" onClick={() => { const p = page - 1; setPage(p); loadMissing(p); }} disabled={page <= 1} className="p-2 rounded-lg btn-ghost-dark text-slate-400 disabled:opacity-30"><ChevronLeft size={14} /></button>
                    <span className="text-[11px] font-mono2 text-slate-500">{page} / {totalPages} · {missing.total} sem capa</span>
                    <button data-testid="capas-next-page" onClick={() => { const p = page + 1; setPage(p); loadMissing(p); }} disabled={page >= totalPages} className="p-2 rounded-lg btn-ghost-dark text-slate-400 disabled:opacity-30"><ChevronRight size={14} /></button>
                  </div>
                )}
              </>
            )}
          </GlassCard>

          <p className="text-[10px] text-slate-600 text-center">As capas que vêm na lista M3U são aplicadas automaticamente na importação — esta página cobre o que veio sem capa. Ao gerar a capa de uma série, ela aplica-se a todos os episódios.</p>
        </>
      )}

      <Modal open={!!manual} onClose={() => setManual(null)} title={`Capa manual — ${manual?.title || ""}`} testid="capa-manual-modal">
        <div className="space-y-3">
          <p className="text-xs text-slate-400">Cole o URL direto de uma imagem (JPG/PNG). Ideal 500×750 (2:3). Será aplicada a este título e a todos os episódios da série.</p>
          <input data-testid="capa-manual-url-input" value={manualUrl} onChange={(e) => setManualUrl(e.target.value)}
            placeholder="https://exemplo.com/capa.jpg" className="input-dark w-full rounded-lg px-3 py-2.5 text-sm font-mono2" />
          {manualUrl.trim().startsWith("http") && (
            <div className="flex justify-center"><img src={manualUrl.trim()} alt="pré-visualização" className="max-h-48 rounded-lg border border-slate-800" data-testid="capa-manual-preview" /></div>
          )}
          <button data-testid="capa-manual-save" onClick={saveManual} disabled={!manualUrl.trim().startsWith("http") || !!busy}
            className="btn-neon w-full py-3 rounded-xl text-sm flex items-center justify-center gap-2">
            {busy ? <Spinner /> : <><Link2 size={14} /> Aplicar capa</>}
          </button>
        </div>
      </Modal>
    </div>
  );
}
